/**
 * Exact numbers for the certificate kernel (#361, K1): the rationals Q and the
 * Gaussian rationals Q(i), both over BigInt. There is no floating point in
 * this folder -- not `number` arithmetic on a value, not `parseFloat`, not
 * `Math.*`. A float that rounds into agreement is a manufactured "equal", and
 * a ✓ built on one is a false accept that nothing downstream can detect.
 * `eslint.config.js` bans the constructs in this folder, and
 * `no-floats.test.ts` scans the sources for them.
 *
 * Every Q is normalised (denominator > 0, gcd 1), so structural equality IS
 * numeric equality and a Q can be compared with `qEq`, keyed by `qKey`.
 */

export interface Q {
  readonly n: bigint;
  readonly d: bigint;
}

/** A Gaussian rational re + im*i. A real number has `im` equal to zero. */
export interface C {
  readonly re: Q;
  readonly im: Q;
}

export class DivisionByZeroError extends Error {
  override readonly name = 'DivisionByZeroError';
  constructor() {
    super('division by zero');
  }
}

export function bigAbs(a: bigint): bigint {
  return a < 0n ? -a : a;
}

export function bigGcd(a: bigint, b: bigint): bigint {
  let x = bigAbs(a);
  let y = bigAbs(b);
  while (y !== 0n) {
    const t = x % y;
    x = y;
    y = t;
  }
  return x;
}

export function bigLcm(a: bigint, b: bigint): bigint {
  if (a === 0n || b === 0n) return 0n;
  return bigAbs((a / bigGcd(a, b)) * b);
}

/** Mathematical modulus: the result is in [0, m). `m` must be positive. */
export function bigMod(a: bigint, m: bigint): bigint {
  const r = a % m;
  return r < 0n ? r + m : r;
}

export function bigFloorDiv(a: bigint, b: bigint): bigint {
  const quotient = a / b;
  return a % b !== 0n && a < 0n !== b < 0n ? quotient - 1n : quotient;
}

export function bigCeilDiv(a: bigint, b: bigint): bigint {
  const quotient = a / b;
  return a % b !== 0n && a < 0n === b < 0n ? quotient + 1n : quotient;
}

export function q(n: bigint, d: bigint = 1n): Q {
  if (d === 0n) throw new DivisionByZeroError();
  let nn = n;
  let dd = d;
  if (dd < 0n) {
    nn = -nn;
    dd = -dd;
  }
  const g = bigGcd(nn, dd);
  return g <= 1n ? { n: nn, d: dd } : { n: nn / g, d: dd / g };
}

export const Q0: Q = { n: 0n, d: 1n };
export const Q1: Q = { n: 1n, d: 1n };

export const qAdd = (a: Q, b: Q): Q => q(a.n * b.d + b.n * a.d, a.d * b.d);
export const qSub = (a: Q, b: Q): Q => q(a.n * b.d - b.n * a.d, a.d * b.d);
export const qMul = (a: Q, b: Q): Q => q(a.n * b.n, a.d * b.d);
export const qNeg = (a: Q): Q => ({ n: -a.n, d: a.d });
export function qDiv(a: Q, b: Q): Q {
  if (b.n === 0n) throw new DivisionByZeroError();
  return q(a.n * b.d, a.d * b.n);
}
export const qEq = (a: Q, b: Q): boolean => a.n === b.n && a.d === b.d;
export const qIsZero = (a: Q): boolean => a.n === 0n;
export const qIsInt = (a: Q): boolean => a.d === 1n;
export function qCmp(a: Q, b: Q): -1 | 0 | 1 {
  const l = a.n * b.d;
  const r = b.n * a.d;
  return l < r ? -1 : l > r ? 1 : 0;
}
export const qKey = (a: Q): string => (a.d === 1n ? a.n.toString() : `${a.n.toString()}/${a.d.toString()}`);
export const qFloor = (a: Q): bigint => bigFloorDiv(a.n, a.d);
export const qCeil = (a: Q): bigint => bigCeilDiv(a.n, a.d);

/** Integer power by repeated squaring; a negative exponent inverts (and throws on zero). */
export function qPow(a: Q, e: bigint): Q {
  if (e < 0n) return qDiv(Q1, qPow(a, -e));
  let result = Q1;
  let base = a;
  let k = e;
  while (k > 0n) {
    if ((k & 1n) === 1n) result = qMul(result, base);
    k >>= 1n;
    if (k > 0n) base = qMul(base, base);
  }
  return result;
}

export const c = (re: Q, im: Q = Q0): C => ({ re, im });
export const C0: C = { re: Q0, im: Q0 };
export const C1: C = { re: Q1, im: Q0 };
export const CI: C = { re: Q0, im: Q1 };
export const cFromInt = (n: bigint): C => ({ re: q(n), im: Q0 });

export const cAdd = (a: C, b: C): C => ({ re: qAdd(a.re, b.re), im: qAdd(a.im, b.im) });
export const cSub = (a: C, b: C): C => ({ re: qSub(a.re, b.re), im: qSub(a.im, b.im) });
export const cNeg = (a: C): C => ({ re: qNeg(a.re), im: qNeg(a.im) });
export const cMul = (a: C, b: C): C => ({
  re: qSub(qMul(a.re, b.re), qMul(a.im, b.im)),
  im: qAdd(qMul(a.re, b.im), qMul(a.im, b.re)),
});
export const cEq = (a: C, b: C): boolean => qEq(a.re, b.re) && qEq(a.im, b.im);
export const cIsZero = (a: C): boolean => qIsZero(a.re) && qIsZero(a.im);
export const cIsReal = (a: C): boolean => qIsZero(a.im);
export function cDiv(a: C, b: C): C {
  const norm = qAdd(qMul(b.re, b.re), qMul(b.im, b.im));
  if (qIsZero(norm)) throw new DivisionByZeroError();
  const num = cMul(a, { re: b.re, im: qNeg(b.im) });
  return { re: qDiv(num.re, norm), im: qDiv(num.im, norm) };
}
export function cPow(a: C, e: bigint): C {
  if (e < 0n) return cDiv(C1, cPow(a, -e));
  let result = C1;
  let base = a;
  let k = e;
  while (k > 0n) {
    if ((k & 1n) === 1n) result = cMul(result, base);
    k >>= 1n;
    if (k > 0n) base = cMul(base, base);
  }
  return result;
}
export const cKey = (a: C): string => (qIsZero(a.im) ? qKey(a.re) : `${qKey(a.re)}+${qKey(a.im)}i`);

/**
 * Parse a non-negative decimal literal ("0.5", "12.250") into an exact
 * rational by string manipulation -- no float is ever formed.
 */
export function qFromDecimalString(text: string): Q {
  const m = /^([0-9]+)\.([0-9]+)$/.exec(text);
  if (m === null) throw new SyntaxError('not a decimal literal');
  const frac = m[2] as string;
  return q(BigInt((m[1] as string) + frac), 10n ** BigInt(frac.length));
}
